import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';

/**
 * Shared email-attachment policy: any number of files, capped by TOTAL size.
 *
 * kula's `Editor` enforces per-file count/size caps and words its tooltip
 * around them ("Attach file (2/5)"), but our email surfaces cap the combined
 * payload instead — so every consumer raises kula's caps out of the way and
 * guards the total itself (mirrors `TemplatedEmail` and `TemplateForm`).
 */

/** The legacy editor's list — kula's default omits spreadsheets, slides and archives. */
export const ATTACHMENT_ACCEPT = DEFAULT_ATTACHMENT_FILES.join(',');

/** Effectively unlimited — kula must not reject on count before our guard. */
export const MAX_ATTACHMENT_FILES = 99;

/** 15MB wire limit minus headroom so rounding can't sneak past it. */
export const MAX_TOTAL_ATTACHMENT_SIZE_MB = 14.9;

/** Per-file passthrough: single files only fail via the total-size guard. */
export const KULA_PER_FILE_PASSTHROUGH_MB = 100;

export const ATTACHMENT_TOOLTIP = 'Upload files less than 15Mb in size';

export const BYTES_PER_MB = 1024 * 1024;

/** Toast payload for a rejected batch — same id everywhere so retries dedupe. */
export const TOTAL_SIZE_TOAST = {
  id: 'attachment-total-size-error',
  title: 'Total file size exceeds limit',
  description: `The total size of all attachments should be less than ${Math.round(
    MAX_TOTAL_ATTACHMENT_SIZE_MB
  )} MB. Please remove some files or choose smaller files.`,
  status: 'error',
} as const;

/** Whether adding `files` on top of `existing` breaches the total cap. */
export const exceedsTotalAttachmentSize = (
  existing: Array<{ size?: number }>,
  files: File[]
): boolean => {
  const existingBytes = existing.reduce(
    (sum, item) => sum + (item.size || 0),
    0
  );
  const newBytes = files.reduce((sum, file) => sum + file.size, 0);
  return (
    (existingBytes + newBytes) / BYTES_PER_MB > MAX_TOTAL_ATTACHMENT_SIZE_MB
  );
};
